import React, { useEffect, useRef, useState } from 'react'
import ReactDOM, { flushSync } from 'react-dom'
import classNames from 'classnames'
import { DAC } from '../interface'
import './popup.css'

export declare interface PopupProps {
  root?(): Element;
  scrollRoot?(): Element;
  children?: React.ReactNode;
  visible?: boolean;
  onVisibleChange?(ev: DAC.Event<boolean>): void;
  trigger?: 'hover' | 'click';
  position?:
    | 'topLeft'
    | 'top'
    | 'topRight'
    | 'rightTop'
    | 'right'
    | 'rightBottom'
    | 'bottomRight'
    | 'bottom'
    | 'bottomLeft'
    | 'leftBottom'
    | 'left'
    | 'leftTop';
  closeOnClickTrigger?: boolean;
  animation?: 'Opacity' | 'Scale' | 'Fade' | string;
  className?: string;
  hiddenOutView?: boolean;
  margin?: number;
}

const Popup = (props: PopupProps) => {
  const {
    children,
    root,
    scrollRoot,
    visible: visibleProp,
    onVisibleChange,
    trigger = 'hover',
    position = 'bottom',
    closeOnClickTrigger = true,
    animation = 'Opacity',
    className,
    hiddenOutView,
    margin,
  } = props
  const [innerVisible, setInnerVisible] = useState(visibleProp)
  useEffect(() => {
    setInnerVisible(visibleProp)
  }, [visibleProp])
  const visible = visibleProp !== undefined ? visibleProp : innerVisible

  const [visibleBoundary, setVisibleBoundary] = useState(true)
  const [isRender, setIsRender] = useState(visibleProp)
  const $popupPlace = useRef<HTMLDivElement>(null)
  const $popup = useRef<HTMLDivElement>(null)

  const positions = () => {
    const $parent = $popupPlace.current?.parentElement
    if (!$parent || !$popup.current) return
    const { top, left, width, height } = $parent.getBoundingClientRect()
    const width2 = $popup.current.clientWidth
    const height2 = $popup.current.clientHeight

    const boundary = (x: number, y: number, w: number, h: number) => {
      let _x = x
      let _y = y
      let visibleFlag = true

      if (_x < 0) {
        _x = 10
        if (hiddenOutView) {
          visibleFlag = false
        }
      }

      if (_x + w > window.innerWidth) {
        _x = window.innerWidth - w - 10
        if (hiddenOutView) {
          visibleFlag = false
        }
      }

      if (_y < 0) {
        _y = 10
        if (hiddenOutView) {
          visibleFlag = false
        }
      }

      if (_y + h > window.innerHeight) {
        _y = window.innerHeight - h - 10
        if (hiddenOutView) {
          visibleFlag = false
        }
      }

      setVisibleBoundary(visibleFlag)
      return { x: _x, y: _y }
    }

    if (position === 'topLeft') {
      return boundary(left, top - height2, width2, height2)
    }
    if (position === 'top') {
      return boundary(
        left + (width - width2) / 2,
        top - height2,
        width2,
        height2,
      )
    }
    if (position === 'topRight') {
      return boundary(left + (width - width2), top - height2, width2, height2)
    }
    if (position === 'rightTop') {
      return boundary(left + width, top, width2, height2)
    }
    if (position === 'right') {
      return boundary(
        left + width,
        top + (height - height2) / 2,
        width2,
        height2,
      )
    }
    if (position === 'rightBottom') {
      return boundary(left + width, top + (height - height2), width2, height2)
    }
    if (position === 'bottomRight') {
      return boundary(left + (width - width2), top + height, width2, height2)
    }
    if (position === 'bottom') {
      return boundary(
        left + (width - width2) / 2,
        top + height,
        width2,
        height2,
      )
    }
    if (position === 'bottomLeft') {
      return boundary(left, top + height, width2, height2)
    }
    if (position === 'leftBottom') {
      return boundary(left - width2, top + (height - height2), width2, height2)
    }
    if (position === 'left') {
      return boundary(
        left - width2,
        top + (height - height2) / 2,
        width2,
        height2,
      )
    }
    if (position === 'leftTop') {
      return boundary(left - width2, top, width2, height2)
    }
  }

  const onClose = (ev: MouseEvent) => {
    const $parent = $popupPlace.current?.parentElement
    if ((ev.target as HTMLElement)?.dataset?.popupClose === 'false') {
      return
    }
    if ($parent?.contains(ev.target as HTMLElement)) {
      return
    }
    if ($popup.current?.contains(ev.target as HTMLElement)) {
      return
    }
    // eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
    close()
  }

  const onScroll = () => {
    const pos = positions()
    $popup.current?.style.setProperty('--zy-popup-x', `${pos?.x}px`)
    $popup.current?.style.setProperty('--zy-popup-y', `${pos?.y}px`)
  }

  const open = () => {
    if (visibleProp === false && !onVisibleChange) {
      return
    }
    if (!isRender) {
      flushSync(() => {
        setIsRender(true)
      })
    }
    setInnerVisible(true)
    onVisibleChange?.({ value: true })

    const pos = positions()
    $popup.current?.style.setProperty('--zy-popup-x', `${pos?.x}px`)
    $popup.current?.style.setProperty('--zy-popup-y', `${pos?.y}px`)
    $popup.current?.style.setProperty('--zy-popup-in', `zyPopup${animation}In`)
    $popup.current?.style.setProperty(
      '--zy-popup-out',
      `zyPopup${animation}Out`,
    )

    if (trigger === 'hover') {
      document.addEventListener('mouseover', onClose)
    }
    document.addEventListener('click', onClose, true);
    (scrollRoot?.() || window).addEventListener('scroll', onScroll)
  }

  const close = () => {
    if (visibleProp && !onVisibleChange) {
      return
    }
    setInnerVisible(false)
    onVisibleChange?.({ value: false })

    if (trigger === 'hover') {
      document.removeEventListener('mouseover', onClose)
    }
    document.removeEventListener('click', onClose);
    (scrollRoot?.() || window).removeEventListener('scroll', onScroll)
  }

  const click = (ev: MouseEvent) => {
    const $parent = $popupPlace.current?.parentElement
    if (!$parent) return
    if ((ev.target as HTMLElement)?.dataset?.popupOpen === 'false') {
      return
    }
    if ($parent.contains(ev.target as HTMLElement)) {
      if (visible && closeOnClickTrigger) {
        close()
      } else {
        open()
      }
    }
  }
  const clickRef = useRef(click)
  useEffect(() => {
    clickRef.current = click
  }, [visible])

  const onResize = () => {
    const pos = positions()
    $popup.current?.style.setProperty('--zy-popup-x', `${pos?.x}px`)
    $popup.current?.style.setProperty('--zy-popup-y', `${pos?.y}px`)
  }

  useEffect(() => {
    const $parent = $popupPlace.current?.parentElement
    if (!$parent) return

    const onClick = (ev: MouseEvent) => clickRef.current(ev)
    if (trigger === 'hover') {
      $parent.addEventListener('mouseenter', open)
    }
    if (trigger === 'click') {
      $parent.addEventListener('click', onClick)
    }
    window.addEventListener('resize', onResize)

    return () => {
      if (trigger === 'hover') {
        $parent.removeEventListener('mouseenter', open)
      }
      if (trigger === 'click') {
        $parent.removeEventListener('click', onClick)
      }
      window.removeEventListener('resize', onResize)
    }
  }, [])

  useEffect(() => {
    if (visibleProp) {
      open()
    } else {
      close()
    }
  }, [visibleProp])

  useEffect(() => {
    if ($popup.current) {
      $popup.current.style.setProperty(
        '--content-margin',
        !!margin || margin === 0 ? `${margin}px` : '8px',
      )
    }
  }, [isRender])

  return (
    <div ref={$popupPlace} style={{ display: 'contents' }}>
      {isRender
        && ReactDOM.createPortal(
          <div
            className={classNames('zyPopupContent', className)}
            ref={$popup}
            onScroll={(ev) => ev.stopPropagation()}
            data-pos={position}
            data-visible={visible && visibleBoundary}
            data-hidden={!visible || !visibleBoundary}
          >
            {children}
          </div>,
          root?.() || document.body,
        )}
    </div>
  )
}

export default Popup
